import type * as api from "@/api/supply";

import { fmtDate, int0 } from "./format";

/** Платное хранение Ozon на строке кластера: одна ячейка (кластер × SKU) с бэка или сумма по
 *  группе. Справка менеджеру — в потребность и движок НЕ входит (решение владельца 15.10.2026:
 *  решает человек руками). */
export interface PaidAgg {
  paid_qty: number;
  qty: number;
  total_day: number;
  cost_30d: number;
  /** различные даты данных входящих ячеек (у юрлиц выгрузка может отставать по-разному) */
  dates: string[];
}

/** Ячейку (или уже собранную сумму) — в агрегат. Без мутации входа: лист и узел дерева не должны
 *  делить один объект. */
export function addPaid(
  acc: PaidAgg | undefined,
  x: api.PaidStorageCell | PaidAgg | null | undefined,
): PaidAgg | undefined {
  if (!x) return acc;
  const xDates = "dates" in x ? x.dates : [x.data_date];
  if (!acc)
    return {
      paid_qty: x.paid_qty,
      qty: x.qty,
      total_day: x.total_day,
      cost_30d: x.cost_30d,
      dates: [...xDates].sort(),
    };
  return {
    paid_qty: acc.paid_qty + x.paid_qty,
    qty: acc.qty + x.qty,
    total_day: acc.total_day + x.total_day,
    cost_30d: acc.cost_30d + x.cost_30d,
    dates: [...new Set([...acc.dates, ...xDates])].sort(),
  };
}

/** Сумма по группе (строка-группа кластера = все входящие кластер × SKU). */
export function sumPaid(
  xs: Iterable<api.PaidStorageCell | PaidAgg | null | undefined>,
): PaidAgg | undefined {
  let acc: PaidAgg | undefined;
  for (const x of xs) acc = addPaid(acc, x);
  return acc;
}

const dec1Trim = (n: number): string =>
  n.toLocaleString("ru-RU", { maximumFractionDigits: 1 });

/** Короткие рубли для плашки: «~850 ₽», «~9 тыс ₽», «~1,2 млн ₽». */
export function rubShort(n: number): string {
  const a = Math.abs(n);
  if (a < 1000) return `~${int0(n)} ₽`;
  if (a < 10_000) return `~${dec1Trim(n / 1000)} тыс ₽`;
  if (a < 1_000_000) return `~${int0(n / 1000)} тыс ₽`;
  return `~${dec1Trim(n / 1_000_000)} млн ₽`;
}

const datesText = (dates: string[]): string =>
  dates.length ? dates.map((d) => fmtDate(d)).join(", ") : "—";

export function paidLabel(p: PaidAgg, horizon = 30): string {
  return `платно ${int0(p.paid_qty)} шт · ${rubShort(p.cost_30d)}/${horizon} дн`;
}

export function paidTitle(p: PaidAgg, horizon = 30): string {
  return [
    `Платное хранение Ozon — данные на ${datesText(p.dates)}`,
    `Платных: ${int0(p.paid_qty)} шт (всего в платных строках: ${int0(p.qty)} шт)`,
    `Начислено: ${int0(p.total_day)} ₽/день`,
    `За ${horizon} дн: ~${int0(p.cost_30d)} ₽`,
    "Оценка при текущем остатке, без учёта продаж. На потребность и «Решение» не влияет — решаете вы.",
  ].join("\n");
}
